import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faRobot, faChevronDown, faFire } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { getEloColor } from '../utils/ladder';
import WeeklyCalendar from '../components/WeeklyCalendar';
import PlayerStatsTooltip from '../components/PlayerStatsTooltip';
import { Activity, Opponent, DailyChallenge, PlayerStats } from '../types/activity';
import { useScrollDismiss } from '../hooks/useScrollDismiss';
import logo from '../assets/logo.png';


interface BoardPlayer {
  id: number;
  display_name: string;
  avatar_url: string;
  is_bot: boolean;
  is_paused?: boolean;
  level_value: number;
  elo_rating: number;
  points: number;
  activity_count: number;
}

interface BoardMatchup {
  matchup_id: number;
  status: string;
  is_my_match: boolean;
  winner_id: number | null;
  player_a: BoardPlayer;
  player_b: BoardPlayer;
  bet_count_a: number;
  bet_count_b: number;
  my_bet_on: number | null;
  my_bet_points: number | null;
}

interface BoardResponse {
  week_label: string | null;
  matchups: BoardMatchup[];
  my_player_id: number;
}

interface BoardDetailPlayer extends BoardPlayer {
  activities: Activity[];
  consolation_points: number;
}

interface BoardDetail {
  cycle: { id: number; week_label: string; start_at_utc: string; workout_deadline_utc: string; };
  is_completed: boolean;
  player_a: BoardDetailPlayer;
  player_b: BoardDetailPlayer;
}

interface LeaderboardPlayer {
  id: number;
  display_name: string;
  avatar_url: string;
  is_bot: boolean;
  is_paused?: boolean;
  level_value: number;
  elo_rating: number;
  elo_games: number;
  wins: number;
  losses: number;
}

interface LeaderboardResponse {
  ranking: LeaderboardPlayer[];
  me: { rank: number; elo_rating: number; elo_games: number };
}

interface Props {
  onBack: () => void;
  onNavigateToPlayerProfile?: (playerId: number) => void;
}

function PlayerAvatar({ player, isWinner, onClick }: { player: BoardPlayer; isWinner: boolean; onClick?: () => void }) {
  const cls = `board-avatar${isWinner ? ' board-avatar--winner' : ''}`;
  const clickProps = onClick ? { onClick, style: { cursor: 'pointer' } as React.CSSProperties } : {};
  if (player.is_bot) return (
    <span className={`${cls} board-avatar--bot`}>
      <FontAwesomeIcon icon={faRobot} />
    </span>
  );
  if (player.avatar_url) return <img src={player.avatar_url} alt={player.display_name} className={cls} {...clickProps} />;
  return <span className={`${cls} board-avatar--placeholder`} {...clickProps}>{player.display_name.charAt(0)}</span>;
}

function formatWeek(label: string | null): string {
  if (!label) return '';
  const m = label.match(/^(\d{4})-W(\d+)$/);
  return m ? `KW\u00a0${m[2]}\u00a0/\u00a0${m[1]}` : label;
}

export default function DuelboardPage({ onBack, onNavigateToPlayerProfile }: Props) {
  const { token } = useAuth();
  const [data, setData] = useState<BoardResponse | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [, forceUpdate] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const detailCache = useRef(new Map<number, BoardDetail | 'loading'>());
  const statsCache = useRef(new Map<number, PlayerStats>());
  const detailStatsCache = useRef(new Map<number, { myStats: PlayerStats | null; oppStats: PlayerStats | null }>());
  const [weekChallenges, setWeekChallenges] = useState<Record<string, DailyChallenge>>({});
  const [hoverPlayer, setHoverPlayer] = useState<{ stats: PlayerStats | null; name: string; x: number; y: number } | null>(null);
  useScrollDismiss(hoverPlayer, setHoverPlayer);
  const [bettingLoading, setBettingLoading] = useState<number | null>(null); // matchup_id being bet on

  // Current day bet points (Mon=7, Sun=1)
  const getDayBetPoints = () => {
    const dow = (new Date().getDay() + 6) % 7; // Mon=0..Sun=6
    return 7 - dow;
  };

  useEffect(() => {
    if (!token) return;
    api.get('/duel/board', token).then((res) => {
      if (res && !res.error && res.matchups) {
        setData(res);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    api.get('/duel/leaderboard', token).then((res) => {
      if (res && res.ranking) setLeaderboard(res);
    }).catch(() => {});
    api.get('/challenges/week', token).then((d) => {
      if (d?.challenges) setWeekChallenges(d.challenges);
    }).catch(() => {});
  }, [token]);

  // ── Betting ──
  function handlePlaceBet(matchupId: number, betOnPlayerId: number) {
    if (!token || bettingLoading !== null) return;
    setBettingLoading(matchupId);
    api.post('/duel/bet', { matchup_id: matchupId, bet_on_player_id: betOnPlayerId }, token).then((res) => {
      if (res?.ok && data) {
        setData({
          ...data,
          matchups: data.matchups.map(m =>
            m.matchup_id === matchupId
              ? { ...m, my_bet_on: betOnPlayerId, my_bet_points: res.bet_points }
              : m
          ),
        });
      }
      setBettingLoading(null);
    }).catch(() => setBettingLoading(null));
  }

  function handlePlayerHover(e: React.MouseEvent, playerId: number, playerName: string) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.bottom + 10;
    if (statsCache.current.has(playerId)) {
      setHoverPlayer({ stats: statsCache.current.get(playerId)!, name: playerName, x, y });
    } else {
      setHoverPlayer({ stats: null, name: playerName, x, y });
      api.get(`/player/${playerId}/stats`, token!).then((data) => {
        if (data && data.wins !== undefined) {
          statsCache.current.set(playerId, data as PlayerStats);
          setHoverPlayer(prev => prev && prev.name === playerName ? { ...prev, stats: data as PlayerStats } : prev);
        }
      }).catch(() => {});
    }
  }

  function handlePlayerLeave() { setHoverPlayer(null); }

  function handleTooltipClick(e: React.MouseEvent, playerId: number, playerName: string) {
    // Toggle tooltip on click/tap (mobile)
    if (hoverPlayer && hoverPlayer.name === playerName) {
      setHoverPlayer(null);
      return;
    }
    handlePlayerHover(e, playerId, playerName);
  }

  function handleRowClick(e: React.MouseEvent, matchupId: number) {
    if (expandedId === matchupId) { setExpandedId(null); return; }
    setExpandedId(matchupId);
    if (!detailCache.current.has(matchupId)) {
      detailCache.current.set(matchupId, 'loading');
      api.get(`/duel/board/${matchupId}`, token!).then((d: BoardDetail) => {
        detailCache.current.set(matchupId, d);
        // Stats für beide Spieler laden
        const loadStats = (pid: number) => {
          if (statsCache.current.has(pid)) return Promise.resolve(statsCache.current.get(pid)!);
          return api.get(`/player/${pid}/stats`, token!).then((res: any) => {
            if (res && res.wins !== undefined) {
              const s = res as PlayerStats;
              statsCache.current.set(pid, s);
              return s;
            }
            return null;
          }).catch(() => null);
        };
        Promise.all([loadStats(d.player_a.id), loadStats(d.player_b.id)]).then(([myS, oppS]) => {
          detailStatsCache.current.set(matchupId, { myStats: myS, oppStats: oppS });
          forceUpdate(n => n + 1);
        });
      });
    }
  }

  function renderExpandedCalendar(m: BoardMatchup) {
    const detail = detailCache.current.get(m.matchup_id);
    if (!detail || detail === 'loading') {
      return <div className="board-detail-loading">Loading calendar…</div>;
    }
    // Player A = links (rot), Player B = rechts (blau) – konsistent mit der Matchup-Zeile
    const leftPlayer  = detail.player_a;
    const rightPlayer = detail.player_b;
    const opponent: Opponent = {
      id: rightPlayer.id,
      display_name: rightPlayer.display_name,
      avatar_url: rightPlayer.avatar_url,
      level_value: rightPlayer.level_value,
      is_bot: rightPlayer.is_bot ? 1 : 0,
      matchup_id: m.matchup_id,
    };
    const dStats = detailStatsCache.current.get(m.matchup_id);
    return (
      <div className="board-detail-panel" onClick={e => e.stopPropagation()}>
        <WeeklyCalendar
          cycleStart={detail.cycle.start_at_utc}
          myActivities={leftPlayer.activities}
          opponentActivities={rightPlayer.activities}
          myName={leftPlayer.display_name}
          myAvatar={leftPlayer.avatar_url || null}
          myIsBot={leftPlayer.is_bot}
          myId={leftPlayer.id}
          onNavigateToPlayerProfile={onNavigateToPlayerProfile}
          opponent={opponent}
          weekChallenges={weekChallenges}
          myConsolationPoints={0}
          opponentConsolationPoints={0}
          hidePoints={true}
          hideSpin
          hideChallengeEarned
          disableChipTooltips
          myStats={dStats?.myStats ?? undefined}
          opponentStats={dStats?.oppStats ?? undefined}
        />
      </div>
    );
  }

  if (loading) return <div className="loading">Loading duelboard…</div>;

  const matchups = (data?.matchups ?? []).filter(m => !m.is_my_match);

  // Build rank map from leaderboard (rank 1 = highest ELO)
  const rankMap = new Map<number, number>();
  if (leaderboard?.ranking) {
    leaderboard.ranking.forEach((p, i) => rankMap.set(p.id, i + 1));
  }

  return (
    <div className="archive-page">
      <header className="archive-header">
        <div className="archive-header-nav">
          <button className="btn-back" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
        </div>
        <img src={logo} alt="NoWeakWeek" className="archive-logo" />
        <h1>Duelboard</h1>
      </header>

      {/* ── Aktuelle Duelle ── */}
      <h2 className="duelboard-section-title">Current Duels</h2>

      {matchups.length === 0 ? (
        <p className="archive-empty">No active duels this week.</p>
      ) : (
        <>
          <div className="archive-stats-bar">
            <span>{formatWeek(data?.week_label ?? null)}</span>
            <span>{matchups.length} Duel{matchups.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="duel-table-wrap">
            {/* Spalten-Header */}
            <div className="duel-table-header">
              <span className="duel-col duel-col--player duel-col--player--left"></span>
              <span className="duel-col duel-col--workouts">Activities</span>
              <span className="duel-col duel-col--colon">vs</span>
              <span className="duel-col duel-col--workouts">Activities</span>
              <span className="duel-col duel-col--player duel-col--player--right"></span>
            </div>

            {/* Duel-Zeilen */}
            {matchups.map(m => {
              const isCompleted = m.status === 'completed';
              const aWon = isCompleted && m.winner_id === m.player_a.id;
              const bWon = isCompleted && m.winner_id === m.player_b.id;
              const aLeading = !isCompleted && m.player_a.activity_count > m.player_b.activity_count;
              const bLeading = !isCompleted && m.player_b.activity_count > m.player_a.activity_count;

              const mkAvatar = (p: BoardPlayer) =>
                p.is_bot
                  ? <span className="duel-avatar duel-avatar--bot"><FontAwesomeIcon icon={faRobot} /></span>
                  : p.avatar_url
                    ? <img src={p.avatar_url} alt="" className="duel-avatar" style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => handlePlayerHover(e, p.id, p.display_name)}
                        onMouseLeave={handlePlayerLeave}
                        onClick={(e) => { e.stopPropagation(); onNavigateToPlayerProfile?.(p.id); }} />
                    : <span className="duel-avatar duel-avatar--ph" style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => handlePlayerHover(e, p.id, p.display_name)}
                        onMouseLeave={handlePlayerLeave}
                        onClick={(e) => { e.stopPropagation(); onNavigateToPlayerProfile?.(p.id); }}>{p.display_name.charAt(0)}</span>;

              return (
                <React.Fragment key={m.matchup_id}>
                <div className={`duel-table-row${expandedId === m.matchup_id ? ' duel-table-row--expanded' : ''}`} onClick={(e) => handleRowClick(e, m.matchup_id)}>
                  {/* Player A (left-aligned): onfire + avatar + name */}
                  <span className={`duel-col duel-col--player duel-col--player--left${aWon ? ' duel-col--winner' : ''}`}>
                    <span className="duel-col-onfire" style={{ color: m.player_a.is_bot ? '#666' : getEloColor(m.player_a.elo_rating ?? 100) }}>
                      {m.player_a.is_bot ? '—' : (m.player_a.elo_rating ?? '—')}
                    </span>
                    {mkAvatar(m.player_a)}
                    <span className="duel-player-name">{m.player_a.display_name}</span>
                    {m.player_a.is_paused && <span className="player-paused-badge" title="pausiert – keine Strava-Verbindung">⏸</span>}
                  </span>
                  <span className={`duel-col duel-col--workouts${aLeading ? ' duel-col--leading' : ''}`}>
                    {m.player_a.activity_count}
                  </span>

                  {/* Status-Trennzeichen */}
                  <span className="duel-col duel-col--colon">
                    <span className={`duel-status-badge${isCompleted ? ' duel-status-badge--done' : ' duel-status-badge--active'}`}>
                      {isCompleted ? (aWon ? '🏆' : bWon ? '🏆' : '🤝') : 'vs'}
                    </span>
                  </span>

                  <span className={`duel-col duel-col--workouts${bLeading ? ' duel-col--leading' : ''}`}>
                    {m.player_b.activity_count}
                  </span>
                  {/* Player B (right-aligned): avatar + name + rank */}
                  <span className={`duel-col duel-col--player duel-col--player--right${bWon ? ' duel-col--winner' : ''}`}>
                    {mkAvatar(m.player_b)}
                    <span className="duel-player-name">{m.player_b.display_name}</span>
                    {m.player_b.is_paused && <span className="player-paused-badge" title="pausiert – keine Strava-Verbindung">⏸</span>}
                    <span className="duel-col-onfire" style={{ color: m.player_b.is_bot ? '#666' : getEloColor(m.player_b.elo_rating ?? 100) }}>
                      {m.player_b.is_bot ? '—' : (m.player_b.elo_rating ?? '—')}
                    </span>
                  </span>
                </div>

                {/* ── Betting Row – ausgeblendet
                {!m.player_a.is_bot && !m.player_b.is_bot && (() => {
                  const betPoints = getDayBetPoints();
                  const hasBet = m.my_bet_on !== null;

                  return (
                    <div key={`bet-${m.matchup_id}`} className="duel-bet-row">
                      {hasBet ? (
                        <div className="duel-bet-info-grid">
                          <span className="duel-bet-col duel-bet-col--left">
                            {m.my_bet_on === m.player_a.id && (
                              <span className="duel-bet-mine">
                                {m.bet_count_a} bets on {m.player_a.display_name} &middot; <span className="duel-bet-pts-pos">Your bet: {m.my_bet_points} pts</span>
                              </span>
                            )}
                          </span>
                          <span className="duel-bet-col duel-bet-col--spacer" />
                          <span className="duel-bet-col duel-bet-col--right">
                            {m.my_bet_on === m.player_b.id && (
                              <span className="duel-bet-mine">
                                {m.bet_count_b} bets on {m.player_b.display_name} &middot; <span className="duel-bet-pts-pos">Your bet: {m.my_bet_points} pts</span>
                              </span>
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className="duel-bet-controls">
                          <button
                            className={`duel-bet-btn${bettingLoading === m.matchup_id ? ' duel-bet-btn--loading' : ''}`}
                            disabled={bettingLoading !== null}
                            onClick={(e) => { e.stopPropagation(); handlePlaceBet(m.matchup_id, m.player_a.id); }}
                          >
                            Bet for {m.player_a.display_name}!
                          </button>
                          <span className="duel-bet-label">Bet on the winner and earn <span className="duel-bet-pts-pos">{betPoints} points</span>, or lose <span className="duel-bet-pts-neg">{betPoints} points</span> for next week if your bet is wrong.</span>
                          <button
                            className={`duel-bet-btn${bettingLoading === m.matchup_id ? ' duel-bet-btn--loading' : ''}`}
                            disabled={bettingLoading !== null}
                            onClick={(e) => { e.stopPropagation(); handlePlaceBet(m.matchup_id, m.player_b.id); }}
                          >
                            Bet for {m.player_b.display_name}!
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
                */}

                  {/* Expandierter Kalender – als Geschwister-Element */}
                  {expandedId === m.matchup_id && (
                    <div key={`detail-${m.matchup_id}`} className="duel-table-detail" onClick={e => e.stopPropagation()}>
                      {renderExpandedCalendar(m)}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}

      {hoverPlayer && (
        <div style={{ position: 'fixed', top: hoverPlayer.y, left: hoverPlayer.x, transform: 'translateX(-50%)', zIndex: 9999, pointerEvents: 'none' }}>
          <PlayerStatsTooltip stats={hoverPlayer.stats} name={hoverPlayer.name} isMine={data?.my_player_id !== undefined && hoverPlayer.name !== ''} />
        </div>
      )}

    </div>
  );
}
